import React, {
  createContext,
  ReactNode,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react'
import { fullscreenApi } from '@sketch/utils'
import { useParams } from 'react-router-dom'
import { RouteParams } from '@sketch/modules-common'
import { CurrentPrototype } from './types'
import { usePrototypeQueryParam } from '../usePrototypeQueryParam'

/**
 * Context for values and methods that are global to components within the
 * PrototypeView.
 */
type PrototypeViewContext = {
  /**
   * Whether fullscreen mode is active. Note that fullscreen mode means either
   * just a hidden header for unsupported browsers, or a hidden header _and_
   * native fullscreen mode active.
   */
  isFullscreen: boolean
  /**
   * Enter fullscreen mode.
   */
  enterFullscreen: () => void
  /**
   * Exit fullscreen mode.
   */
  exitFullscreen: () => void
  /**
   * Whether the exit fullscreen button should be visible. There are a few subtleties
   * here - we don't show this button by default when the user enters fullscreen
   * mode. We only want to show it if the user clicks outside of hotspots, and
   * if the user isn't in a native fullscreen mode (because in this mode the browser
   * will usually provide its own UI to exit fullscreen mode).
   */
  showExitFullscreenButton: boolean
  /**
   * Hide the exit fullscreen button.
   */
  toggleOffExitFullscreenButton: () => void
  /**
   * Callback that can be passed into the prototype player, it defines behaviour
   * to trigger when the user clicks outside any hotspot in the player.
   */
  onUnhandledClickInteraction: () => void
  currentPrototype: CurrentPrototype
}

const context = createContext<PrototypeViewContext | undefined>(undefined)

type FullscreenProviderProps = {
  children: ReactNode
}

export function PrototypeViewContextProvider({
  children,
}: FullscreenProviderProps) {
  // Initialize the `isFullscreen` value based on whether the user is currently
  // in fullscreen mode already when they arrive on the page
  const [isFullscreen, setIsFullscreen] = useState(
    () => fullscreenApi.fullscreenElement() !== null
  )

  const [
    isExitFullscreenButtonToggled,
    setIsExitFullscreenButtonToggled,
  ] = useState(false)

  const enterFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.requestFullscreen(document.documentElement, {
        navigationUI: 'hide',
      })
    } catch (e) {
      // An error here means the browser doesn't support native fullscreen.
      // We can ignore it however, and set `isFullscreen` to `true` regardless
      // since we still want to hide the header
    }
    setIsFullscreen(true)
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.exitFullscreen()
    } catch (e) {
      // Ignore for same reason as above
    }
    setIsFullscreen(false)
  }, [])

  // User can enter and exit fullscreen mode without calling our functions by
  // using native browser buttons or shortcuts - so we still need to detect
  // this happening and sync our `active` value
  useEffect(() => {
    function onFullscreenChanged() {
      setIsFullscreen(Boolean(fullscreenApi.fullscreenElement()))
    }

    if (fullscreenApi.changeEvent) {
      document.addEventListener(fullscreenApi.changeEvent, onFullscreenChanged)
    }

    return () => {
      if (fullscreenApi.changeEvent) {
        document.removeEventListener(
          fullscreenApi.changeEvent,
          onFullscreenChanged
        )
      }
    }
  }, [])

  const onUnhandledClickInteraction = useCallback(() => {
    if (isFullscreen) {
      setIsExitFullscreenButtonToggled(value => !value)
    }
  }, [isFullscreen])

  const toggleOffExitFullscreenButton = useCallback(() => {
    setIsExitFullscreenButtonToggled(false)
  }, [])

  const showExitFullscreenButton =
    isFullscreen &&
    fullscreenApi.fullscreenElement() === null &&
    isExitFullscreenButtonToggled

  const currentPrototype = useKeepTrackOfCurrentPrototype()

  return (
    <context.Provider
      value={{
        currentPrototype,
        isFullscreen,
        exitFullscreen,
        enterFullscreen,
        showExitFullscreenButton,
        onUnhandledClickInteraction,
        toggleOffExitFullscreenButton,
      }}
    >
      {children}
    </context.Provider>
  )
}

export function usePrototypeViewContext() {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'usePrototypeViewContext must be used within a PrototypeViewContextProvider'
    )
  }

  return contextValue
}

/**
 * Create a CurrentPrototype object based on the parameters of the URL that we will then be
 * set in the PrototypeViewContext for subcomponent to read.
 *
 * Note: We are using a "?prototype=:startPointArtboardId" to always keep track
 * of the prototype that was opened by the user and always show a prototype artboard in context
 * of a specific start point. For legacy reason, this parameter
 * can be missing as this "prototype" query string param has been introduced later.
 * When the parameter is missing we say that the current prototype is "orphan" as we are not able
 * to associated the prototype artboard URL with a specific prototype in the document.
 * "Orphan prototypes" is an edge case that will appear less and less but that we need to support for now as
 * some prototype links were created without any prototype parameter.
 * In the future we could try to move it directly to a URL parameter instead of a query string.
 */
function useKeepTrackOfCurrentPrototype(): CurrentPrototype {
  const { artboardUUID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()
  const initialArtboardIdRef = useRef(artboardUUID)

  const prototypeStartPointArtboardId = usePrototypeQueryParam()

  return useMemo((): CurrentPrototype => {
    if (prototypeStartPointArtboardId) {
      return {
        type: 'prototype',
        prototypeId: prototypeStartPointArtboardId,
        startPointArtboardId: prototypeStartPointArtboardId,
        currentArtboardId: artboardUUID,
      }
    }

    // The id of the artboard the user first landed on. Since in this scenario we don't know
    // the exact starting point of the prototype, we will use that as a fallback
    // starting point.
    const initialArtboardId = initialArtboardIdRef.current
    return {
      type: 'orphan_prototype',
      prototypeId: null,
      currentArtboardId: artboardUUID,
      initialArtboardId,
    }
  }, [artboardUUID, prototypeStartPointArtboardId])
}
